import { type FormikErrors, type FormikTouched, useFormik } from 'formik'
import { useEffect, useRef } from 'react'
import * as Yup from 'yup'

import { DatePicker, Select, DateHelper } from '@loadsmart/loadsmart-ui'
import type { SelectDatasource } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { Drawer, Field, Icon, LabeledValue, Layout, Steps } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import {
  type OpenDockWarehouseDetails,
  type Carrier,
  type OpenDockLoadTypes,
} from '../../domain/Shipment'
import { useGetOpenDockWarehouseAvailability } from '../hooks/useGetOpenDockWarehouseAvailability'
import { ScheduleTimeList } from '../ScheduleTimeList/ScheduleTimeList'
import { CustomOption } from './CustomOption'
import { ErrorMessage } from './ErrorMessage'
import { NoCallsNeededBanner } from './NoCallsNeededBanner'
import { OpenDockSignUpBanner } from './OpenDockSignUpBanner'
import { RectangleSkeleton } from './RectangleSkeleton'

function buildLoadTypeDataSource(loadTypes: Array<OpenDockLoadTypes>) {
  return (): SelectDatasource<OpenDockLoadTypes> => {
    return {
      type: 'loadType',
      adapter: {
        getKey: item => item.id,
        getLabel: (item: OpenDockLoadTypes) =>
          `${item.operation} - ${item.equipmentType} - ${item.mode} - ${item.duration}m`,
      },
      fetch({ regex }) {
        return loadTypes.filter(({ name }) => regex.test(name))
      },
    }
  }
}

//change some string values to shape
const Schema = Yup.object().shape({
  loadType: Yup.object<OpenDockLoadTypes>().required('Required'),
  dock: Yup.object<{ value: string; label: string }>().required('Required'),
  appointmentDate: Yup.string().required('Required'),
  appointmentTime: Yup.string().required('Required'),
})

export type ScheduleFormValues = {
  loadType: OpenDockLoadTypes | null
  dock: { value: string; label: string } | null
  appointmentDate: string | null
  appointmentTime: string | null
}

const TODAY = DateHelper()

function getValidationStatus(
  fieldName: keyof ScheduleFormValues,
  errors: FormikErrors<ScheduleFormValues>,
  touched: FormikTouched<ScheduleFormValues>
) {
  if (touched[fieldName] && errors[fieldName]) {
    return 'danger'
  }

  return 'default'
}

export function Schedule({
  isSignedUp,
  warehouseDetails,
  shipmentCarrier,
  shipmentUUID,
  stopIndex,
  setStep,
  fields,
  setStaticFields,
  facilityTimezone,
}: {
  isSignedUp: boolean
  warehouseDetails: OpenDockWarehouseDetails
  shipmentCarrier: Carrier
  shipmentUUID: string
  stopIndex: number
  setStep: (step: string) => void
  fields: {
    staticFields: ScheduleFormValues
    dynamicFields: Record<string, number | string | boolean | null>
  }
  setStaticFields: (staticFields: ScheduleFormValues) => void
  facilityTimezone?: string
}) {
  const dialogBodyRef = useRef<React.ComponentRef<typeof Drawer.Body>>(null)

  const { handleChange, values, setFieldValue, errors, touched, setFieldTouched, isValid } =
    useFormik<ScheduleFormValues>({
      initialValues: fields.staticFields,
      onSubmit: () => undefined,
      validationSchema: Schema,
      validateOnMount: true,
    })

  const {
    warehouseAvailability,
    fetchOpenDockWarehouseAvailability,
    status: warehouseAvailabilityRequestStatus,
  } = useGetOpenDockWarehouseAvailability()

  const isLoadingAvailability = warehouseAvailabilityRequestStatus === RequestStatus.Pending
  const showDocks = Boolean(values.loadType?.id)
  const showAppointmentDate = Boolean(values.loadType?.id && values.dock?.value)
  const showScheduleTimeList = Boolean(
    values.loadType?.id &&
      values.dock?.value &&
      values.appointmentDate &&
      warehouseAvailabilityRequestStatus === RequestStatus.Done
  )

  useEffect(() => {
    if (dialogBodyRef.current && warehouseAvailabilityRequestStatus === RequestStatus.Done) {
      const frameId = requestAnimationFrame(() => {
        dialogBodyRef.current?.scrollBy({
          top: 1000,
          left: 0,
          behavior: 'smooth',
        })
      })

      return () => cancelAnimationFrame(frameId)
    }
  }, [dialogBodyRef, warehouseAvailabilityRequestStatus])

  return (
    <>
      <Drawer.Body ref={dialogBodyRef}>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <Steps activeStep="schedule" style={{ gap: '0' }}>
            <Steps.Step name="schedule">Schedule</Steps.Step>
            <Steps.Step name="send-info">Send Info</Steps.Step>
          </Steps>

          {isSignedUp ? (
            <NoCallsNeededBanner shipmentCarrier={shipmentCarrier} />
          ) : (
            <OpenDockSignUpBanner shipmentCarrier={shipmentCarrier} />
          )}
          <Layout.Stack gap="spacing-4">
            <LabeledValue>
              <LabeledValue.Label>Warehouse</LabeledValue.Label>
              <LabeledValue.Value>{warehouseDetails.name}</LabeledValue.Value>
            </LabeledValue>
            <Field required status={getValidationStatus('loadType', errors, touched)}>
              <Field.Label>Load type</Field.Label>
              <Select
                name="loadType"
                placeholder="Select load type"
                datasources={[buildLoadTypeDataSource(warehouseDetails.loadTypes)]}
                value={values.loadType as Selectable | null}
                components={{
                  Option: CustomOption,
                }}
                onChange={event => {
                  handleChange(event)
                  setFieldValue('dock', null)
                }}
                onBlur={() => setFieldTouched('loadType')}
              ></Select>
              <ErrorMessage
                validationStatus={getValidationStatus('loadType', errors, touched)}
                errors={errors}
                fieldName="loadType"
              />
            </Field>
            {showDocks && (
              <Field required status={getValidationStatus('dock', errors, touched)}>
                <Field.Label>Docks</Field.Label>
                <Select
                  name="dock"
                  placeholder="Select docks"
                  value={values.dock as Selectable | null}
                  options={values.loadType?.docks.map(dock => ({
                    value: dock.id,
                    label: dock.name,
                  }))}
                  onChange={event => {
                    handleChange(event)
                    setFieldValue('appointmentDate', null)
                  }}
                  onBlur={() => setFieldTouched('dock')}
                  disabled={!values.loadType}
                />
                <ErrorMessage
                  validationStatus={getValidationStatus('dock', errors, touched)}
                  errors={errors}
                  fieldName="dock"
                />
              </Field>
            )}
            {showAppointmentDate && (
              <Field required status={getValidationStatus('appointmentDate', errors, touched)}>
                <Field.Label>Appointment date</Field.Label>
                <DatePicker
                  name="appointmentDate"
                  value={values.appointmentDate}
                  onChange={event => {
                    setFieldTouched('appointmentDate')
                    setFieldValue('appointmentTime', null)

                    handleChange(event)

                    if (values.loadType?.id && values.dock?.value && event.target.value) {
                      fetchOpenDockWarehouseAvailability(shipmentUUID, stopIndex, {
                        loadTypeId: values.loadType.id,
                        dockId: values.dock.value,
                        appointmentDate: event.target.value,
                        facilityTimezone,
                      })
                    }
                  }}
                  constraints={[[null, TODAY.subtract('day', 1).toString()]]}
                />
                <ErrorMessage
                  validationStatus={getValidationStatus('appointmentDate', errors, touched)}
                  errors={errors}
                  fieldName="appointmentDate"
                />
              </Field>
            )}
            {isLoadingAvailability && <RectangleSkeleton />}
            {showScheduleTimeList && (
              <ScheduleTimeList
                timeSlots={warehouseAvailability}
                setValue={(value: string | null) => {
                  setFieldTouched('appointmentTime')
                  setFieldValue('appointmentTime', value)
                }}
                value={values.appointmentTime}
                validationStatus={getValidationStatus('appointmentTime', errors, touched)}
                errorMessage={errors.appointmentTime}
              />
            )}
          </Layout.Stack>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary onClick={() => setStep('already_scheduled')}>
          Already scheduled in Opendock
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={!isValid}
          trailing={<Icon name="arrow-right" color="color-text-inverted" />}
          onClick={() => {
            setStep('send_info')
            setStaticFields(values)
          }}
        >
          Next
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
